import type {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
} from "react";
import { Icon, type IconName } from "./Icon";
import { STATE_META } from "@/lib/format";
import type { Service } from "@/lib/types";

type Tone = "success" | "warning" | "destructive" | "info";

const TONE_COLORS: Record<
  Tone,
  { bg: string; fg: string; dot: string }
> = {
  success: {
    bg: "color-mix(in oklch, var(--success) 14%, transparent)",
    fg: "color-mix(in oklch, var(--success) 75%, #000)",
    dot: "var(--success)",
  },
  warning: {
    bg: "color-mix(in oklch, var(--warning) 22%, transparent)",
    fg: "color-mix(in oklch, var(--warning) 60%, #000)",
    dot: "var(--warning)",
  },
  destructive: {
    bg: "color-mix(in oklch, var(--destructive) 16%, transparent)",
    fg: "var(--destructive)",
    dot: "var(--destructive)",
  },
  info: {
    bg: "color-mix(in oklch, var(--info) 16%, transparent)",
    fg: "var(--info)",
    dot: "var(--info)",
  },
};

interface StatusPillProps {
  state: string;
  size?: "sm" | "lg";
}

export function StatusPill({ state, size = "sm" }: StatusPillProps) {
  const meta = STATE_META[state] ?? STATE_META.operational;
  const colors = TONE_COLORS[meta.tone];
  const h = size === "lg" ? 24 : 20;
  const isActive = state !== "operational" && state !== "resolved";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: h,
        padding: "0 10px",
        borderRadius: 999,
        fontSize: size === "lg" ? 12 : 11,
        fontWeight: 500,
        background: colors.bg,
        color: colors.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: colors.dot,
          boxShadow: isActive
            ? `0 0 0 3px color-mix(in oklch, ${colors.dot} 25%, transparent)`
            : "none",
        }}
      />
      {meta.label}
    </span>
  );
}

interface ServiceMarkProps {
  service: Pick<Service, "initials" | "swatch">;
  size?: number;
}

export function ServiceMark({ service, size = 32 }: ServiceMarkProps) {
  const fontSize = Math.max(9, Math.round(size * 0.34));
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: Math.round(size * 0.22),
        background: service.swatch,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize,
        letterSpacing: "-0.02em",
        flexShrink: 0,
        boxShadow:
          "inset 0 -1px 0 rgba(0,0,0,.15), 0 0 0 1px rgba(0,0,0,.04)",
      }}
    >
      {service.initials}
    </span>
  );
}

interface CardProps {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}

export function Card({ children, style, className }: CardProps) {
  return (
    <div
      className={className}
      style={{
        background: "var(--card)",
        borderRadius: 14,
        boxShadow:
          "0 0 0 1px color-mix(in oklch, var(--foreground) 8%, transparent)",
        overflow: "hidden",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  right?: ReactNode;
  style?: CSSProperties;
}

export function CardHeader({
  title,
  description,
  right,
  style,
}: CardHeaderProps) {
  return (
    <div
      style={{
        padding: "14px 16px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        borderBottom: "1px solid var(--border)",
        gap: 12,
        ...style,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}
        >
          {title}
        </div>
        {description && (
          <div
            style={{
              fontSize: 12,
              color: "var(--muted-foreground)",
              marginTop: 2,
            }}
          >
            {description}
          </div>
        )}
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: "primary" | "outline" | "ghost" | "destructive" | "secondary";
  size?: "sm" | "md" | "lg";
  icon?: IconName | string;
  href?: string;
  type?: "button" | "submit" | "reset";
  children?: ReactNode;
}

const SIZES = {
  sm: { h: 28, px: 10, fs: 12 },
  md: { h: 32, px: 12, fs: 13 },
  lg: { h: 38, px: 14, fs: 14 },
};

const VARIANTS = {
  primary: {
    background: "var(--primary)",
    color: "var(--primary-foreground)",
    borderColor: "transparent",
  },
  outline: {
    background: "var(--background)",
    color: "var(--foreground)",
    borderColor: "var(--border)",
  },
  ghost: {
    background: "transparent",
    color: "var(--foreground)",
    borderColor: "transparent",
  },
  destructive: {
    background: "var(--destructive)",
    color: "var(--destructive-foreground)",
    borderColor: "transparent",
  },
  secondary: {
    background: "var(--secondary)",
    color: "var(--secondary-foreground)",
    borderColor: "transparent",
  },
};

export function Button({
  variant = "outline",
  size = "md",
  icon,
  children,
  onClick,
  style,
  type = "button",
  href,
  disabled,
  title,
  ...rest
}: ButtonProps) {
  const s = SIZES[size];
  const v = VARIANTS[variant];
  const baseStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: s.h,
    padding: `0 ${s.px}px`,
    fontSize: s.fs,
    borderRadius: 8,
    fontWeight: 500,
    border: "1px solid",
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "inherit",
    textDecoration: "none",
    whiteSpace: "nowrap",
    opacity: disabled ? 0.5 : 1,
    ...v,
    ...style,
  };
  if (href) {
    return (
      <a href={href} style={baseStyle} title={title}>
        {icon && <Icon name={icon} size={s.fs + 1} />}
        {children}
      </a>
    );
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={baseStyle}
      {...rest}
    >
      {icon && <Icon name={icon} size={s.fs + 1} />}
      {children}
    </button>
  );
}

export function Spinner({ size = 12 }: { size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: "2px solid color-mix(in oklch, currentColor 30%, transparent)",
        borderTopColor: "currentColor",
        display: "inline-block",
        animation: "sw-spin 0.8s linear infinite",
        marginRight: 4,
      }}
    />
  );
}

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}

export function Field({ label, hint, children }: FieldProps) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: "var(--foreground)",
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
          {hint}
        </span>
      )}
    </label>
  );
}

interface InputProps {
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  style?: CSSProperties;
  mono?: boolean;
  readOnly?: boolean;
  type?: string;
}

export function Input({
  value,
  onChange,
  placeholder,
  style,
  mono,
  readOnly,
  type = "text",
}: InputProps) {
  return (
    <input
      type={type}
      value={value}
      readOnly={readOnly}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      style={{
        height: 36,
        padding: "0 12px",
        borderRadius: 8,
        border: "1px solid var(--input)",
        background: "var(--background)",
        color: "var(--foreground)",
        fontFamily: mono ? "var(--font-mono)" : "inherit",
        fontSize: mono ? 12 : 13,
        outline: "none",
        ...style,
      }}
    />
  );
}

export const SEVERITY_BAR_COLOR = (sev: string) =>
  sev === "major" || sev === "major-outage"
    ? "var(--destructive)"
    : sev === "degraded" || sev === "minor"
      ? "var(--warning)"
      : sev === "maintenance"
        ? "var(--info)"
        : "var(--success)";
